<template>
	<div>
		<SectionCard title="Cosa vuoi provare" icon="mdi-tune-variant">
			<div class="sf-label">Leva</div>
			<v-btn-toggle :value="value.lever" mandatory dense color="primary" class="sf-toggle" @change="set('lever', $event)">
				<v-btn small value="discount"><v-icon small left>mdi-percent-outline</v-icon>Sconto</v-btn>
				<v-btn small value="price" :disabled="value.scopeType !== 'items' || value.items.length !== 1"><v-icon small left>mdi-currency-eur</v-icon>Nuovo prezzo</v-btn>
				<v-btn small value="shipping"><v-icon small left>mdi-truck-fast-outline</v-icon>Spedizione gratis</v-btn>
			</v-btn-toggle>
			<div v-if="value.lever === 'price'" class="sf-muted mt-1">
				Solo su un prodotto: il nuovo prezzo può essere anche più alto di quello attuale<template v-if="currentPrice"> ({{ euroCent(currentPrice) }}, prima variante)</template>.
			</div>

			<v-row class="mt-3" dense>
				<v-col cols="12" sm="6">
					<v-text-field
						:value="value.amount"
						type="number"
						:label="labelValore"
						:suffix="value.lever === 'discount' ? '%' : '€'"
						outlined
						dense
						hide-details="auto"
						:min="value.lever === 'discount' ? 1 : 0"
						:max="value.lever === 'discount' ? 90 : undefined"
						@input="set('amount', $event)"
					/>
				</v-col>
				<v-col cols="12" sm="6">
					<v-select :value="value.days" :items="GIORNI" label="Per quanti giorni" outlined dense hide-details @change="set('days', $event)" />
				</v-col>
			</v-row>
		</SectionCard>

		<SectionCard v-if="value.lever !== 'shipping'" title="Su quali prodotti" icon="mdi-shopping-outline" class="mt-4">
			<v-btn-toggle :value="value.scopeType" mandatory dense color="primary" class="sf-toggle" @change="cambiaAmbito">
				<v-btn small value="items">Prodotti</v-btn>
				<v-btn small value="category">Categoria</v-btn>
				<v-btn small value="brand">Marca</v-btn>
			</v-btn-toggle>

			<v-autocomplete
				v-if="value.scopeType === 'items'"
				:value="value.items"
				:items="prodottiVisibili"
				:search-input.sync="cerca"
				:loading="cercando"
				multiple
				no-filter
				return-object
				small-chips
				deletable-chips
				item-value="_id"
				item-text="name"
				label="Prodotti (scrivi almeno 3 lettere)"
				prepend-inner-icon="mdi-magnify"
				outlined
				dense
				hide-details="auto"
				class="mt-3"
				@change="sceltiProdotti"
			/>
			<v-autocomplete
				v-else-if="value.scopeType === 'category'"
				:value="value.category"
				:items="categorie"
				item-value="_id"
				item-text="string"
				label="Categoria"
				prepend-inner-icon="mdi-sitemap-outline"
				outlined
				dense
				hide-details="auto"
				clearable
				class="mt-3"
				@change="set('category', $event)"
			/>
			<v-autocomplete
				v-else
				:value="value.brand"
				:items="marche"
				item-value="_id"
				item-text="name"
				label="Marca"
				prepend-inner-icon="mdi-tag-outline"
				outlined
				dense
				hide-details="auto"
				clearable
				class="mt-3"
				@change="set('brand', $event)"
			/>
		</SectionCard>
	</div>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import { euroCent } from '../formato';

export const GIORNI = [
	{ value: 7, text: '7 giorni' },
	{ value: 14, text: '14 giorni' },
	{ value: 30, text: '30 giorni' },
	{ value: 60, text: '60 giorni' },
];

const ricerca = val => ({ options: { sortBy: ['created_at'], sortDesc: [true], page: 1, itemsPerPage: 20 }, filters: '', search: val });

// riga della ricerca prodotti (una per variante) -> un prodotto solo, con nome e SKU
const daRiga = r => ({ _id: String(r._id), name: `${(r.trans && r.trans.name) || ''} ${(r.variants && r.variants.sku) || ''}`.trim(), sku: (r.variants && r.variants.sku) || '', price: r.variants && r.variants.pricevat });

// Parametri del simulatore: leva, valore, giorni e ambito. Il v-model e' un oggetto unico, aggiornato per campo.
export default {
	name: 'SimulatorForm',
	components: { SectionCard },
	props: {
		value: { type: Object, required: true },
		categorie: { type: Array, default: () => [] },
		marche: { type: Array, default: () => [] },
		// prezzo usato dal calcolo per il prodotto scelto (prima variante): base della leva "nuovo prezzo"
		currentPrice: { type: Number, default: null },
	},
	data() {
		return { cerca: '', cercando: false, risultati: [], GIORNI };
	},
	computed: {
		labelValore() {
			if (this.value.lever === 'discount') return 'Sconto';
			if (this.value.lever === 'price') return 'Nuovo prezzo (IVA inclusa)';
			return 'Spedizione gratis sopra';
		},
		// i prodotti scelti restano nella lista anche quando la ricerca cambia
		prodottiVisibili() {
			return _.uniqBy([...this.value.items, ...this.risultati], '_id');
		},
	},
	watch: {
		cerca: _.debounce(function (val) {
			if (!val || val.length < 3) return;
			this.cercando = true;
			this.$store
				.dispatch('items/asyncSearchData', ricerca(val))
				.then(rows => {
					this.risultati = _.uniqBy((rows || []).map(daRiga), '_id');
				})
				.finally(() => (this.cercando = false));
		}, 250),
	},
	methods: {
		euroCent,
		set(campo, valore) {
			const next = { ...this.value, [campo]: valore };
			// passando a "nuovo prezzo" si parte dal prezzo attuale, cambiando leva da li' si torna a un valore sensato
			if (campo === 'lever' && valore === 'price' && this.currentPrice) next.amount = this.currentPrice;
			if (campo === 'lever' && valore === 'discount' && this.value.lever !== 'discount') next.amount = 10;
			if (campo === 'lever' && valore === 'shipping' && this.value.lever !== 'shipping') next.amount = 49;
			// il nuovo prezzo vale solo su un prodotto: cambiando ambito si torna allo sconto
			if (next.lever === 'price' && (next.scopeType !== 'items' || next.items.length !== 1)) next.lever = 'discount';
			this.$emit('input', next);
		},
		cambiaAmbito(tipo) {
			this.set('scopeType', tipo);
		},
		sceltiProdotti(lista) {
			this.cerca = '';
			this.set('items', lista || []);
		},
	},
};
</script>

<style scoped>
.sf-label {
	font-size: 12px;
	color: #5f6b7a;
	margin-bottom: 4px;
}
.sf-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.sf-toggle {
	flex-wrap: wrap;
}
</style>
